import React from 'react';
import PlayingCard from './PlayingCard';
import { Trans, useTranslation } from 'react-i18next';
import { tr } from '../../../../i18n/msg';
import {
  formatClock, formatGram, REASON_TEXT, stakePrize,
} from '../utils/cards';

/** Lobby, searching and result screens of the Durak page. */

export function GramIcon() {
  return <img className="dk-gram" src="/gram.png" alt="GRAM" />;
}

function formatAgo(t, value) {
  const ts = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(ts)) return '';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return t('time.just_now', 'just now');
  if (s < 3600) return t('time.minutes_ago', '{{n}}m ago', { n: Math.floor(s / 60) });
  if (s < 86400) return t('time.hours_ago', '{{n}}h ago', { n: Math.floor(s / 3600) });
  return t('time.days_ago', '{{n}}d ago', { n: Math.floor(s / 86400) });
}

const HERO_CARDS = ['AS', 'KH', 'QD', 'JC'];

export function DurakLobby({
  info, loading, error, stake, setStake, onFind, onInvite, onRetry, notice, onPassRequired, banner,
}) {
  const { t } = useTranslation();
  if (loading && !info) return <div className="dk-center"><div className="dk-spinner" /></div>;
  if (error && !info) {
    return (
      <div className="dk-center">
        <div className="dk-muted">{error}</div>
        <button type="button" className="app-btn app-btn--purple app-btn--sm dk-auto" onClick={onRetry}>{t('common.retry', 'Retry')}</button>
      </div>
    );
  }
  if (!info) return null;
  if (info.enabled === false) {
    return <div className="dk-center"><div className="dk-muted">{t('durak.closed', 'Durak tables are closed right now. Come back later!')}</div></div>;
  }
  const stakes = info.stakes || ['0'];
  const balance = Number(info.tonBalance) || 0;
  const commission = Number(info.commissionPercent) || 0;
  const stakeNum = Number(stake) || 0;
  const canAfford = stakeNum <= balance;
  const prize = stakePrize(stakeNum, commission);
  // GRAM stakes are DatumPass-only (free practice is open to everyone)
  const hasPass = info.datumPassActive === true;

  return (
    <div className="dk-lobby">
      <div className="dk-hero" aria-hidden="true">
        {HERO_CARDS.map((c, i) => (
          <div key={c} className="dk-hero__card" style={{ '--i': i }}><PlayingCard code={c} /></div>
        ))}
      </div>

      {banner}
      {notice && <div className="dk-notice">{notice}</div>}

      <section className="dk-section">
        <div className="dk-section__row">
          <h3 className="dk-section__title">{t('arena.stake', 'Stake')}</h3>
          <span className="dk-balance"><GramIcon />{formatGram(balance)} GRAM</span>
        </div>
        <div className="dk-stakes">
          {stakes.map((s) => {
            const n = Number(s) || 0;
            const locked = n > 0 && !hasPass;
            return (
              <button
                key={s}
                type="button"
                className={`dk-stake${String(stake) === String(s) ? ' is-selected' : ''}${n === 0 ? ' dk-stake--free' : ''}`}
                disabled={n > balance && !locked}
                onClick={() => (locked ? onPassRequired() : setStake(String(s)))}
              >
                {n === 0 ? t('arena.free', 'Free') : (<><GramIcon />{formatGram(n)}</>)}
                {locked && <span className="stake-lock" aria-label={t('arena.pass_required_aria', 'DatumPass required')}>🔒</span>}
              </button>
            );
          })}
        </div>
        <div className="dk-prize">
          {stakeNum === 0 ? (
            <span>
              {t('durak.practice', 'Practice game — no GRAM at stake')}
              {!hasPass && ` · 🔒 ${t('durak.stakes_need_pass', 'GRAM stakes need DatumPass')}`}
            </span>
          ) : (
            <>
              <span>{t('durak.winner_takes', 'Winner takes')}</span>
              <b><GramIcon />{formatGram(prize)} GRAM</b>
              <span className="dk-muted">{t('arena.fee', '({{pct}}% fee)', { pct: commission })}</span>
            </>
          )}
        </div>
      </section>

      <button type="button" className="app-btn app-btn--green dk-find" disabled={!canAfford} onClick={onFind}>
        🃏 {t('arena.find_opponent', 'Find opponent')}
      </button>
      <button type="button" className="app-btn app-btn--blue inv-friend-btn" disabled={!canAfford} onClick={onInvite}>
        👥 {t('pvp.play_with_friend', 'Play with a friend')}
      </button>

      <details className="dk-rules">
        <summary>{t('durak.rules.title', 'How to play')}</summary>
        <ul>
          <li><Trans i18nKey="durak.rules.deck" defaults="36 cards (6 → A). The bottom card of the deck is the <b>trump</b>; it beats any other suit." components={{ b: <b /> }} /></li>
          <li>{t('durak.rules.attack', 'The attacker plays any card. The defender beats it with a higher card of the same suit or with a trump.')}</li>
          <li>{t('durak.rules.throw_in', 'The attacker may throw in more cards of ranks already on the table — up to 6 and never more than the defender holds.')}</li>
          <li><Trans i18nKey="durak.rules.take" defaults="Can't (or don't want to) beat? <b>Take</b> all the cards — you attack again later." components={{ b: <b /> }} /></li>
          <li><Trans i18nKey="durak.rules.bito" defaults="All beaten → <b>Бито</b>: the cards are discarded and the defender attacks next. Everyone refills to 6 from the deck." components={{ b: <b /> }} /></li>
          <li><Trans i18nKey="durak.rules.end" defaults="When the deck is empty, the first player out of cards wins. The last one holding cards is the <b>Durak</b>." components={{ b: <b /> }} /></li>
          <li>{t('durak.rules.timer', '30 s per move. Three missed moves in a row or 60 s offline = loss.')}</li>
        </ul>
      </details>

      {info.recent?.length > 0 && (
        <section className="dk-section">
          <h3 className="dk-section__title">{t('durak.recent', 'Recent games')}</h3>
          <ul className="dk-recent">
            {info.recent.map((r) => {
              let result = 'DRAW';
              if (r.won === true) result = 'WIN';
              else if (r.won === false) result = 'LOSS';
              const resultText = {
                WIN: t('arena.res.win', 'WIN'), LOSS: t('arena.res.loss', 'LOSS'), DRAW: t('arena.res.draw', 'DRAW'),
              }[result];
              return (
                <li key={r.id} className="dk-recent__row">
                  <span className={`dk-recent__res dk-recent__res--${result.toLowerCase()}`}>{resultText}</span>
                  <span className="dk-recent__opp">{t('arena.vs', 'vs {{name}}', { name: r.opponentName })}</span>
                  <span className="dk-recent__stake">
                    {Number(r.stake) > 0 ? <>{formatGram(r.stake)} GRAM</> : t('arena.free', 'Free')}
                  </span>
                  <span className="dk-recent__ago">{formatAgo(t, r.endedAt)}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}

export function DurakSearching({
  queue, connection, onCancel, joining,
}) {
  const { t } = useTranslation();
  const connecting = connection !== 'open';
  const free = Number(queue?.stake) === 0;
  return (
    <div className="dk-center dk-search">
      <div className="dk-shuffle" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="dk-shuffle__card" style={{ '--i': i }}><PlayingCard faceDown /></div>
        ))}
      </div>
      <div className="dk-search__title">
        {!queue && (joining ? t('pvp.joining_friend', 'Joining your friend…') : t('durak.rejoining', 'Rejoining your game…'))}
        {queue && (connecting ? t('pvp.connecting', 'Connecting…') : t('durak.looking', 'Looking for an opponent…'))}
      </div>
      {queue && <div className="dk-search__time">{formatClock(queue.waitedSec || 0)}</div>}
      {queue && (
        <div className="dk-muted">
          {free ? t('durak.free_table', 'Free table') : (
            <Trans
              i18nKey="durak.stake_real_players"
              defaults="Stake <b>{{stake}}</b> GRAM — only real players"
              values={{ stake: formatGram(queue.stake) }}
              components={{ b: <b /> }}
            />
          )}
        </div>
      )}
      {free && queue?.botAfterSec != null && queue.botAfterSec > 0 && (
        <div className="dk-muted">{t('durak.bot_joins', 'A practice bot joins in {{n}}s', { n: queue.botAfterSec })}</div>
      )}
      <button type="button" className="app-btn app-btn--purple dk-auto" onClick={onCancel}>{t('common.cancel', 'Cancel')}</button>
    </div>
  );
}

export function DurakResult({
  end, match, onAgain, onLobby,
}) {
  const { t } = useTranslation();
  const draw = end.winner === null || end.winner === undefined;
  const won = !draw && end.winner === end.you;
  let title = t('durak.result.draw', 'Draw');
  if (won) title = t('durak.result.victory', 'Victory!');
  else if (!draw) title = t('durak.result.durak', 'You are the Durak');
  const payout = Number(end.payout) || 0;
  const refund = Number(end.refund) || 0;
  let reason = tr(t, REASON_TEXT[end.reason]) || end.reason;
  if (end.reason === 'WIN') {
    reason = won
      ? t('durak.reason.you_out', 'You got rid of all your cards')
      : t('durak.reason.opp_out', 'Opponent got rid of all cards first');
  } else if (end.reason === 'FORFEIT' || end.reason === 'TIMEOUT' || end.reason === 'DISCONNECT') {
    reason = t('durak.reason.who', '{{who}}: {{reason}}', {
      who: won ? t('brawl.opponent', 'Opponent') : t('brawl.you', 'You'),
      reason: tr(t, REASON_TEXT[end.reason]).toLowerCase(),
    });
  }
  let kind = 'lose';
  if (won) kind = 'win';
  else if (draw) kind = 'draw';
  const emoji = { win: '🏆', draw: '🤝', lose: '🃏' }[kind];
  return (
    <div className={`dk-center dk-result dk-result--${kind}`}>
      <div className="dk-result__emoji" aria-hidden="true">{emoji}</div>
      <h2 className="dk-result__title">{title}</h2>
      <div className="dk-muted">{reason}</div>
      {payout > 0 && <div className="dk-result__money">+{formatGram(payout)} <GramIcon /> GRAM</div>}
      {refund > 0 && <div className="dk-result__refund">{t('durak.stake_returned', 'Stake returned: {{amount}} GRAM', { amount: formatGram(refund) })}</div>}
      {end.error === 'SETTLEMENT_PENDING' && <div className="dk-muted">{t('durak.payout_processing', 'Payout is being processed…')}</div>}
      {end.oppHand?.length > 0 && (
        <div className="dk-result__opp">
          <div className="dk-muted">{t('durak.opp_cards', "{{name}}'s cards", { name: match?.opponent?.name || t('brawl.opponent', 'Opponent') })}</div>
          <div className="dk-result__cards">
            {end.oppHand.map((c) => <div key={c} className="dk-result__card"><PlayingCard code={c} /></div>)}
          </div>
        </div>
      )}
      <div className="dk-result__actions">
        <button type="button" className="app-btn app-btn--green" onClick={onAgain}>{t('durak.play_again', 'Play again')}</button>
        <button type="button" className="app-btn app-btn--purple" onClick={onLobby}>{t('durak.lobby', 'Lobby')}</button>
      </div>
    </div>
  );
}
